import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';
import { cloneDeep } from 'es-toolkit';
import validate from 'validate.js';

import {
  Box,
  DateInput,
  DatePicker,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Modal,
  Switch,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';

import {
  useRentalManagerUserPropertyFloorPlanDeleteWebApiHandler_delete,
  useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_edit,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails, useGetTours } from '../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import useRentZestimate from '../../../hooks/useRentZestimate';
import accessUtils from '../../../utils/accessUtils';
import { postingConstraints, removeErrorKey } from '../../../utils/formValidation';
import numberUtils from '../../../utils/numberUtils';
import { getIsInputFormDisabled } from '../../MFAccordion/utils';

import NotificationConstants from '../../../constants/NotificationConstants';

import TourCardEditable from '../../PostingPath/MediaContent/ListingTourFormField/components/TourCardEditable';
import TourCardEmpty from '../../PostingPath/MediaContent/ListingTourFormField/components/TourCardEmpty';

import InlineStaticAlert from '../../Alerts/InlineStaticAlert';
import { useAuth } from '../../AuthProvider';
import VideoFormField from '../../PostingPath/MediaContent/VideoFormField';
import ZestimateHelper from '../../PostingPath/ZestimateHelper';
import RMWInput from '../../RMWInput';
import ModalFooter from '../ModalFooter';

import type { KeyboardEvent } from 'react';
import type { ToastPropsInterface } from '@zillow/constellation-10';
import type { GetUserPropertyDetailsFloorPlanUnitWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const NAME = 'name';
const UNIT_NAME = 'unitName';
const SQ_FT = 'squareFeet';
const PRICE = 'price';

type Props = {
  // floorplan index can be undefined for orphaned units
  floorPlanIndex?: number;
  onClose: () => void;
  unitIndexToEdit: number;
};

const UnitModal = ({ floorPlanIndex, onClose, unitIndexToEdit }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { floorplans = [], units = [] } = useGetTours(listingAlias, listingDetails);

  const floorPlan = floorPlanIndex !== undefined ? floorplans[floorPlanIndex] : undefined;
  const unitOfInterest = floorPlan ? floorPlan.units[unitIndexToEdit] : units[unitIndexToEdit];

  const hasAnalyticsRentComps = listingDetails?.hasAnalyticsRentComps ?? false;
  const isFeed = listingDetails?.isFeed ?? false;
  const isPartialListingManaged = listingDetails?.partialListing?.isPartialListingManaged ?? false;
  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);
  const isUnitInputFormDisabled = isInputFormDisabled || isPartialListingManaged;
  const isUnitMediaDisabled = isFeed
    ? listingDetails?.isArchived || listingDetails?.restrictions?.isUpdateListingRestricted
    : isInputFormDisabled;

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const [unit, setUnit] = useState<GetUserPropertyDetailsFloorPlanUnitWebDto & { price?: string }>(
    cloneDeep({
      ...unitOfInterest,
      price: numberUtils.formatPriceFromApi(unitOfInterest.lowPrice?.toString(), unitOfInterest.highPrice?.toString()),
    }),
  );
  const [unitErrors, setUnitErrors] = useState<Record<string, string>>({});
  const [genericError, setGenericError] = useState('');
  const [dateAvailable, setDateAvailable] = useState('');
  const [isModified, setIsModified] = useState(false);

  const startOfYesterday = new Date(new Date(new Date()).getTime() - 24 * 3600 * 1000);

  const rentZestimate = useRentZestimate({
    hasAnalyticsRentComps,
    street: listingDetails?.street || '',
    city: listingDetails?.city || '',
    state: listingDetails?.state || '',
    zip: listingDetails?.zip || '',
    unitName: unit?.name,
  });

  const { mutateAsync: editUnit, isPending: isEditingUnit } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_edit,
    successToastMessage: 'Unit updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'edit unit' },
    },
  });

  const { mutateAsync: deleteUnit, isPending: isDeletingUnit } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanDeleteWebApiHandler_delete,
    successToastMessage: 'Unit deleted',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'delete unit' },
    },
  });

  const isSubmitting = isEditingUnit || isDeletingUnit;

  useEffect(() => {
    const initDateAvailable = new Date(`${unitOfInterest?.dateAvailable}T10:00:00`);
    if (isValid(initDateAvailable)) {
      setDateAvailable(format(initDateAvailable, 'MM/dd/yyyy'));
    }
  }, [unitOfInterest?.dateAvailable]);

  useEffect(() => {
    const newDateAvailable = new Date(dateAvailable);

    if (isValid(newDateAvailable)) {
      setUnit({ ...unit, dateAvailable: format(newDateAvailable, 'yyyy-MM-dd') });
    } else {
      // @ts-expect-error - intentionally setting dateAvailable to null to avoid API update
      setUnit({ ...unit, dateAvailable: null });
    }
  }, [dateAvailable]);

  const handleSubmit = async () => {
    if (!isModified) {
      onClose();
      return;
    }

    const { name, price, squareFeet, dateAvailable, active } = unit;

    try {
      await editUnit({
        postData: {
          unitId: unit.id,
          name,
          price: price || '',
          ...(squareFeet && { squareFeet: Number(squareFeet) }),
          dateAvailable: dateAvailable || '',
          active,
        },
      });

      enqueueToast(<Toast tone="success">Unit updated</Toast>);
      onClose();
    } catch (error) {
      const err = error as {
        error?: {
          message?: string;
          data?: {
            errorByFieldName?: Record<string, string>;
          };
        };
      };

      if (err.error && err.error.data && err.error.data.errorByFieldName) {
        setUnitErrors(err.error.data.errorByFieldName);
      } else if (err.error && err.error.message === 'An existing unit already exists with this unit number') {
        setGenericError(err.error.message);
      } else {
        setGenericError('Something went wrong, refresh the page and try again');
      }
    }
  };

  const validateField = (fieldName: string, fieldValue: string | number) => {
    const error = validate(
      { [fieldName]: fieldValue },
      { [fieldName]: postingConstraints[fieldName as keyof typeof postingConstraints] },
    )?.[fieldName]?.[0];
    if (error) {
      setUnitErrors({
        ...unitErrors,
        [fieldName]: removeErrorKey(fieldName, error),
      });
      return true;
    }
    return false;
  };

  const handleUnitChange = (key: string, value: string | { target: { name: string; value: string } } | boolean) => {
    if (!isModified) {
      setIsModified(true);
    }

    if (genericError) {
      setGenericError('');
    }

    const valueToValidate = typeof value === 'string' || typeof value === 'boolean' ? value : value.target.value;

    switch (key) {
      case NAME:
        if (validateField(UNIT_NAME, String(valueToValidate))) return;
        break;
      case SQ_FT:
        if (validateField(SQ_FT, Number(valueToValidate))) return;
        break;
      case PRICE:
        if (validateField(PRICE, Number(valueToValidate))) return;
        break;
      default:
        break;
    }

    setUnit({
      ...unit,
      [key]: valueToValidate,
    });

    if (unitErrors[key]) {
      setUnitErrors({
        ...unitErrors,
        [key]: '',
      });
    }
  };

  const handleClearFieldError = useCallback(
    (event: { target: { name: string } }) => {
      const { name: fieldName } = event.target;

      if (unitErrors[fieldName]) {
        setUnitErrors({ ...unitErrors, [fieldName]: '' });
      }
    },
    [unitErrors],
  );

  const handleDateAvailableChange = (newDate: string | Date) => {
    if (!isModified) {
      setIsModified(true);
    }

    setDateAvailable(newDate instanceof Date ? format(newDate, 'MM/dd/yyyy') : newDate);
  };

  const handleDeleteClick = async () => {
    try {
      await deleteUnit({ getParams: { floorPlanId: unit.id } });
      onClose();
    } catch (error) {
      enqueueToast(<Toast tone="critical">{error?.message || 'Unit couldn’t be removed. Try again.'}</Toast>);
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'Space') {
      (event.target as HTMLInputElement).click();
    }
  };

  return (
    <Modal
      useDismissProps={{ outsidePress: false }}
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>Edit unit</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <RMWInput
              data-testid="unit-number"
              disabled={isUnitInputFormDisabled || isSubmitting}
              isRequired={!isUnitInputFormDisabled}
              errorMessage={unitErrors.name}
              fieldName="name"
              flex="1 1 50%"
              inputType="adorned"
              initialValue={unit?.name}
              label="Unit number"
              leftAdornment="#"
              clearFieldError={handleClearFieldError}
              onBlur={(value) => handleUnitChange('name', value)}
            />

            <RMWInput
              data-testid="price"
              disabled={isUnitInputFormDisabled || isSubmitting}
              isRequired={!isUnitInputFormDisabled}
              errorMessage={unitErrors.price}
              fieldName="price"
              flex="1 1 50%"
              initialValue={String(unit?.price)}
              inputType="adorned"
              label={canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Price'}
              leftAdornment="$"
              rightAdornment="/mo"
              clearFieldError={handleClearFieldError}
              onBlur={(value) => handleUnitChange('price', value)}
            >
              {hasAnalyticsRentComps && (
                <ZestimateHelper data-testid="estimate" rentZestimate={rentZestimate} showAnalyze={true} />
              )}
            </RMWInput>
          </Box>

          <InlineStaticAlert />

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <Box css={{ flex: '1 1 50%' }}>
              <FormField
                disabled={isUnitInputFormDisabled || isSubmitting}
                label={
                  <Label>
                    Date available
                    {canAccessTotalMonthlyPriceWithFee && (
                      <>
                        {' '}
                        <Text css={{ color: '{colors.legacy.coolGray.500}' }} textStyle="body-sm">
                          (optional)
                        </Text>
                      </>
                    )}
                  </Label>
                }
                control={
                  <DatePicker
                    input={<DateInput data-testid="date-available" />}
                    value={dateAvailable}
                    onValueChange={handleDateAvailableChange}
                    minDate={startOfYesterday}
                    disabled={isUnitInputFormDisabled || isSubmitting}
                  />
                }
                error={!!unitErrors.dateAvailable}
                description={unitErrors.dateAvailable && <FormHelp>{unitErrors.dateAvailable}</FormHelp>}
              />
            </Box>

            <RMWInput
              data-testid="square-feet"
              disabled={isUnitInputFormDisabled || isSubmitting}
              showOptional={canAccessTotalMonthlyPriceWithFee}
              errorMessage={unitErrors.squareFeet}
              fieldName="squareFeet"
              flex="1 1 50%"
              initialValue={unit?.squareFeet ? String(unit?.squareFeet) : ''}
              label="Square footage"
              clearFieldError={handleClearFieldError}
              onBlur={(value) => handleUnitChange('squareFeet', value)}
            />
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <Box className="UnitModal-switch-container" css={{ flex: '1 1 50%' }}>
              <LabeledControl
                disabled={isUnitInputFormDisabled || isSubmitting}
                aria-label="Unit available"
                tabIndex={0}
                role="button"
                size="lg"
                controlId="Unit-available"
                label={<Label>Unit available</Label>}
                labelPosition="left"
                control={
                  <Switch
                    disabled={isUnitInputFormDisabled || isSubmitting}
                    data-testid="UnitModal-switch"
                    checked={Boolean(unit?.active)}
                    onChange={() => handleUnitChange('active', !unit.active)}
                    onKeyUp={handleKeyUp}
                  />
                }
              />

              {unitErrors.active ? (
                <FormHelp error={true}>{unitErrors.active}</FormHelp>
              ) : (
                <Box css={{ minHeight: '400' }} />
              )}
            </Box>

            <Box css={{ display: 'flex', flex: '1 1 50%' }}>
              <TextButton
                disabled={isUnitInputFormDisabled || isSubmitting}
                onClick={handleDeleteClick}
                icon={<IconDeleteOutline />}
              >
                Delete unit
              </TextButton>
            </Box>
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '0 1 50%' }}>
              <Label size="lg">3D tour</Label>

              {!unitOfInterest?.vrModel?.vrModelId && !unitOfInterest?.externalTour?.tourUrl ? (
                <TourCardEmpty
                  isDisabled={isUnitMediaDisabled}
                  floorPlanIndex={floorPlanIndex}
                  unitIndex={unitIndexToEdit}
                  listingToursCount={0}
                  tourType="unit"
                />
              ) : (
                <TourCardEditable
                  vrModel={{ ...unitOfInterest?.vrModel, creatorZuid: Number(unitOfInterest?.vrModel?.creatorZuid) }}
                  externalTour={unitOfInterest?.externalTour}
                  tourType="unit"
                  hasBigBottomBorder={false}
                  isDisabled={isUnitMediaDisabled}
                  floorPlanIndex={floorPlanIndex}
                  unitIndex={unitIndexToEdit}
                  updateStatusMessage={(message: string, status = NotificationConstants.SUCCESS) =>
                    enqueueToast(<Toast tone={status as ToastPropsInterface['tone']}>{message}</Toast>)
                  }
                />
              )}
            </Box>

            <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '0 1 50%' }}>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
                <Label size="lg">Video</Label>

                <VideoFormField unitId={unit?.id} unitVideoUrl={unit?.videoUrl} isDisabled={isUnitMediaDisabled} />
              </Box>
            </Box>
          </Box>
        </Box>
      }
      footer={
        <ModalFooter
          submitText="Done"
          onSubmit={handleSubmit}
          errorMessage={genericError}
          hasError={!!genericError}
          isSubmitting={isSubmitting}
          isSubmitDisabled={Boolean(
            !unit?.name || !unit?.price || isSubmitting || unitErrors.price || unitErrors.squareFeet || unitErrors.name,
          )}
        />
      }
    />
  );
};

export { UnitModal };
export default UnitModal;
